import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';

import { cn } from '../shared';
import {
  iconRegistry,
  type AppIconName,
  type IconEntry,
  type IconLoader,
  type IconWeight,
  type RegistryIconProps,
} from './registry';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZE_CLASS: Record<IconSize, string> = {
  xs: 'size-icon-xs',
  sm: 'size-icon-sm',
  md: 'size-icon',
  lg: 'size-icon-lg',
  xl: 'size-icon-xl',
  '2xl': 'size-icon-2xl',
};

// Built once at module scope. Calling lazy() during render produces a new
// component type every time, which remounts the subtree on each render.
//
// This is the shared half of the registration: wrapping a loader for React.lazy
// and caching the result. It lives here, not in the registry, because a helper
// that owned the import() would hide which export each entry uses and cost the
// whole family in one chunk.
const lazyByName = new Map<string, ComponentType<RegistryIconProps>>();

const toLazy = (load: IconLoader) => lazy(async () => ({ default: await load() }));

for (const [name, entry] of Object.entries<IconEntry>(iconRegistry)) {
  if ('load' in entry) lazyByName.set(name, toLazy(entry.load));
}

export interface IconProps {
  name: AppIconName;
  /** Omitted, the glyph takes its parent's font size — which is usually right. */
  size?: IconSize;
  weight?: IconWeight;
  className?: string;
  /**
   * Decorative beside a label, meaningful alone. There is no default: an icon
   * whose role the call site has not decided is one nobody has thought about.
   */
  'aria-hidden'?: boolean | 'true' | 'false';
  /**
   * The other half of that decision: an icon carrying meaning on its own needs a
   * name. Both reach the glyph through the same rest spread below — declaring it
   * is what makes that reachable from the type rather than only from reading the
   * implementation. (TypeScript exempts hyphenated JSX attributes from excess-property
   * checks, so this passed undeclared; that is a reason to write it down, not to rely on it.)
   */
  'aria-label'?: string;
  /** Shown while the glyph loads. A blank frame beats a spinner that flashes. */
  fallback?: ReactNode;
}

export function Icon({ name, size, weight, className, fallback = null, ...aria }: IconProps) {
  // cn returns '' when it is given nothing, and React would emit class="".
  const classes = cn(size ? SIZE_CLASS[size] : undefined, className) || undefined;
  const entry = iconRegistry[name] as IconEntry | undefined;
  const Eager = entry && !('load' in entry) ? entry : undefined;
  const Glyph = Eager ?? lazyByName.get(name);

  // Reached only when the name came from data rather than a literal, since the
  // prop type rejects an unknown literal at compile time. Visible on purpose:
  // a silent null shifts layout in production and nobody sees the console.
  if (!Glyph) {
    return (
      <span
        {...aria}
        data-icon-missing={name}
        className={cn('inline-block rounded-sm border-control border-current', classes)}
      />
    );
  }

  // Built rather than spread inline: exactOptionalPropertyTypes distinguishes an
  // absent weight from one explicitly set to undefined, and the vendor's is optional.
  const glyphProps: RegistryIconProps = { ...aria };
  if (classes) glyphProps.className = classes;
  if (weight) glyphProps.weight = weight;

  const glyph = <Glyph {...glyphProps} />;
  return Eager ? glyph : <Suspense fallback={fallback}>{glyph}</Suspense>;
}
